import { create } from "zustand"
import { persist } from "zustand/middleware"
import { GeometryQCStatus } from "../models/MissionReview"
import { Order, SortOrder } from "../models/SortOrder"

export enum DistanceUnits {
    m = "m",
    nMi = "nMi",
    km = "km",
    mi = "mi",
}

export enum HeightUnits {
    m = "m",
    km = "km",
    mi = "mi",
    ft = "ft",
}

export enum TimeConvention {
    TWELVE = "TWELVE",
    TWENTY_FOUR = "TWENTY_FOUR",
}

interface LocalStorageState {
    setOffices: (offices: string[]) => void
    offices: string[]
    setPreferredDistanceUnit: (preferredDistanceUnit: DistanceUnits) => void
    preferredDistanceUnit: DistanceUnits
    setPreferredHeightUnit: (preferredHeightUnit: HeightUnits) => void
    preferredHeightUnit: HeightUnits
    showInactive: boolean
    setShowInactive: (showInactive: boolean) => void
    setTableSortOrder: (tableName: string, orderBy: string | number | symbol, order?: Order) => void
    tableSortOrders: { [key: string]: SortOrder<string | number | symbol> }
    setTimeConvention: (timeSupports24Format: TimeConvention) => void
    timeConvention: TimeConvention
    missionReviewEventStatusAction?: GeometryQCStatus
    setMissionReviewEventStatusAction: (status: GeometryQCStatus) => void
}

export const useLocalStorageStore = create<LocalStorageState>()(
    persist(
        (set, get) => ({
            setOffices: (offices: string[]) => set({ offices }),
            offices: [],
            setPreferredDistanceUnit: (preferredDistanceUnit: DistanceUnits) => set({ preferredDistanceUnit }),
            preferredDistanceUnit: DistanceUnits.m,
            setPreferredHeightUnit: (preferredHeightUnit: HeightUnits) => set({ preferredHeightUnit }),
            preferredHeightUnit: HeightUnits.m,
            setShowInactive: (showInactive) => set({ showInactive }),
            showInactive: false,
            setTableSortOrder: (tableName: string, orderBy, order = Order.asc) => {
                const copiedOrders = { ...get().tableSortOrders }
                copiedOrders[tableName] = { orderBy, order }
                set({ tableSortOrders: copiedOrders })
            },
            tableSortOrders: {},
            setTimeConvention: (timeConvention) => set({ timeConvention }),
            timeConvention: TimeConvention.TWENTY_FOUR,
            setMissionReviewEventStatusAction: (missionReviewEventStatusAction: GeometryQCStatus) =>
                set({ missionReviewEventStatusAction }),
        }),
        {
            name: "ZLocalStorageStore",
            // Everytime we make a breaking change to the store we need to update the version of the store.
            version: 1,
            // And add migration to update the old version of state
            migrate: (persistedStateUnknown, version) => {
                const persistedState = persistedStateUnknown as LocalStorageState
                if (version === 0 && persistedState.timeConvention) {
                    persistedState.timeConvention = TimeConvention.TWENTY_FOUR
                }
                return persistedState
            },
        },
    ),
)
